import { useLayoutEffect, useSyncExternalStore } from "react";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";
export type ThemeScope = "workspace" | "light";

const preferenceKey = "knowledge:theme";
const media = window.matchMedia("(prefers-color-scheme: dark)");
const listeners = new Set<() => void>();
const parsePreference = (value: string | null): ThemePreference =>
  value === "light" || value === "dark" ? value : "system";
function readPreference() {
  try {
    return parsePreference(localStorage.getItem(preferenceKey));
  } catch {
    return "system" as const;
  }
}
let preference = readPreference();
let scope: ThemeScope =
  document.documentElement.dataset.themeScope === "light" ? "light" : "workspace";
function resolve(): ResolvedTheme {
  if (scope === "light") return "light";
  return preference === "system" ? (media.matches ? "dark" : "light") : preference;
}
let snapshot = { preference, resolvedTheme: resolve() };

function apply() {
  const resolvedTheme = resolve();
  document.documentElement.dataset.theme = resolvedTheme;
  document.documentElement.dataset.themeScope = scope;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", resolvedTheme === "dark" ? "#17221d" : "#f7f4e8");
  if (
    snapshot.preference === preference &&
    snapshot.resolvedTheme === resolvedTheme
  ) return;
  snapshot = { preference, resolvedTheme };
  listeners.forEach((listener) => listener());
}
media.addEventListener("change", apply);
window.addEventListener("storage", (event) => {
  if (event.key !== preferenceKey && event.key !== null) return;
  preference = readPreference();
  apply();
});
apply();

export function setThemePreference(value: ThemePreference) {
  preference = value;
  try {
    localStorage.setItem(preferenceKey, value);
  } catch {
    // The choice still applies for this session when storage is unavailable.
  }
  apply();
}
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export function useTheme() {
  return { ...useSyncExternalStore(subscribe, () => snapshot), setThemePreference };
}

/** Scope the whole document, including portals, without changing the preference. */
export function useThemeScope(value: ThemeScope) {
  useLayoutEffect(() => {
    scope = value;
    apply();
  }, [value]);
}
